/**
 * AgentProgressUIChatTimeline Component
 *
 * Timeline-based status tracking interface for CHAT payments.
 * Displays real-time payment progress with event timeline.
 *
 * Features:
 * - Payment link display with copy functionality
 * - Real-time SSE event updates
 * - Timeline visualization (Link created → Payment complete)
 * - Icon indicators: ⏰ (pending) → ✓ (completed) → ✗ (failed)
 * - Cancel and Complete actions
 * - Mock data support
 * - Fully PCI descoped (no card data displayed)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { formatTimestamp } from '@utils/formatters';
import { UX_DELAYS } from '@utils/constants';
import type {
  AgentProgressUIChatTimelineProps,
  ChatSessionData,
  TimelineEvent,
  TimelineEventType,
  ChatErrorScreenConfig,
  PaymentMethodType,
} from '@/types/chat-timeline.types';
import type {
  SSESubscription,
  TimelineEventData,
  PaymentFailedData,
  FieldCompletedData,
  PaymentCompleteData,
  PaymentProgressData,
} from '@/types/sse.types';
import {
  mapFieldToTimelineEvent,
  getFieldStatusText,
} from '@utils/eventMapper';
import { CheckIcon, ClockIcon, CrossIcon, CopyIcon, InfoIcon } from './icons';
import { useAgentApi } from '@hooks/useAgentApi';
import { ErrorScreen } from './screens/ErrorScreen';
import { mockScenarios } from '../../mocks/mockChatTimelineData';
import { logger } from '@utils/logger';
import { handleSessionTermination } from '@utils/sessionTermination';
import styles from './AgentProgressUIChatTimeline.module.css';

// Events that can only occur inside the Checkout UI — if any of these is
// completed/failed, the link must have been opened, even if the LINK_OPENED
// SSE was skipped (e.g. direct-to-new-card SPA routing).
// SESSION_EXPIRED and SESSION_CANCELLED are intentionally excluded — they can
// fire before the customer ever opens the link.
const IMPLIES_LINK_OPENED = new Set<TimelineEventType>([
  'PAYMENT_METHOD_SELECTED',
  'CARD_NUMBER_ENTERED',
  'CARDHOLDER_NAME_ENTERED',
  'EXPIRY_ENTERED',
  'CVC_ENTERED',
  'PAYMENT_PROCESSING',
  'PAYMENT_COMPLETE',
]);

// Returns the 4-step baseline timeline used when resetting after a retry.
// LINK_CREATED preserves its original timestamp; remaining steps are fresh.
function buildBaselineTimeline(
  linkCreatedEvent: TimelineEvent | undefined,
  sessionId: string,
  now: string
): TimelineEvent[] {
  return [
    {
      eventType: 'LINK_CREATED',
      status: 'Link created',
      timestamp: linkCreatedEvent?.timestamp ?? now,
      completed: true,
      transactionId: sessionId,
    },
    {
      eventType: 'LINK_OPENED',
      status: 'Link opened',
      timestamp: now,
      completed: true,
      transactionId: sessionId,
    },
    {
      eventType: 'PAYMENT_METHOD_SELECTED',
      status: 'Payment method selected',
      timestamp: now,
      completed: false,
      transactionId: sessionId,
    },
    {
      eventType: 'PAYMENT_COMPLETE',
      status: 'Payment complete',
      timestamp: now,
      completed: false,
      transactionId: sessionId,
    },
  ];
}

// setSessionData updater shared by the LINK_OPENED deferred reset (Block 1) and
// the synchronous flush (Block 2). Guards: terminal success and internal SPA
// navigation (method already selected, no failure, no card data entered yet).
function applyLinkOpenedReset(
  prev: ChatSessionData | null,
  sessionId: string,
  hasDeclined = false
): ChatSessionData | null {
  if (!prev) return prev;
  const hasTerminalSuccess = prev.timeline.some(
    (e) => e.eventType === 'PAYMENT_COMPLETE' && e.completed
  );
  if (hasTerminalSuccess) return prev;
  const methodSelected = prev.timeline.find(
    (e) => e.eventType === 'PAYMENT_METHOD_SELECTED'
  );
  const paymentHasFailed = prev.timeline.some(
    (e) => e.eventType === 'PAYMENT_COMPLETE' && e.failed
  );
  // Skip reset for internal / → /new-card navigation: the Checkout UI
  // fires LINK_OPENED on new-card page mount right after the customer
  // selected NEW_CARD on the method-selection screen. That is NOT a retry —
  // the method step is already completed but no card form attempt has been
  // made yet. Guard is scoped to NEW_CARD only: saved card and APM declines
  // never trigger this internal navigation, so LINK_OPENED arriving after
  // their decline must always reset the timeline.
  // hasDeclined covers the PAS-32466 case where CARD_DECLINED does not add
  // a timeline entry (intermediate decline — session still in progress).
  const cardFormCompleted = prev.timeline.some(
    (e) => e.eventType === 'CARD_NUMBER_ENTERED' && e.completed
  );
  if (
    methodSelected?.completed &&
    !paymentHasFailed &&
    !hasDeclined &&
    !cardFormCompleted &&
    methodSelected.paymentMethod === 'NEW_CARD'
  )
    return prev;
  const linkCreatedEvent = prev.timeline.find(
    (e) => e.eventType === 'LINK_CREATED'
  );
  const now = new Date().toISOString();
  return {
    ...prev,
    paymentStatus: 'INITIATED',
    timeline: buildBaselineTimeline(linkCreatedEvent, sessionId, now),
  };
}

export const AgentProgressUIChatTimeline = ({
  sessionId,
  customerId,
  amount: _amount,
  currency: _currency,
  useMockData = false,
  mockScenario = 'new-card',
  sseUrl,
  onSessionComplete,
  onSessionCancel,
  onError,
  initialSession,
  postMessageTargetOrigin = '*',
}: AgentProgressUIChatTimelineProps) => {
  // Agent API integration
  const {
    apiClient,
    isReady,
    error: authError,
  } = useAgentApi({
    agentSessionId: sessionId,
    initialSession,
    skip: useMockData,
  });

  useEffect(() => {
    if (authError) onError?.(authError);
  }, [authError, onError]);

  useEffect(() => {
    if (!useMockData && postMessageTargetOrigin === '*') {
      logger.error(
        '[ChatTimeline] postMessageTargetOrigin is "*" in real mode — set it to the host CRM origin to prevent broadcasting paymentId to all origins'
      );
    }
  }, [useMockData, postMessageTargetOrigin]);

  const [sessionData, setSessionData] = useState<ChatSessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [declineCount, setDeclineCount] = useState(0);
  const [errorScreen, setErrorScreen] = useState<ChatErrorScreenConfig | null>(
    null
  );
  const sseSubscriptionRef = useRef<SSESubscription | null>(null);
  const completionEmittedRef = useRef(false);
  const hasDeclinedRef = useRef(false);
  const progressionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const linkOpenedResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const sessionDataRef = useRef(sessionData);
  useEffect(() => {
    sessionDataRef.current = sessionData;
  }, [sessionData]);

  // One-way latch: false → true when session loads; never goes back to false.
  const isSessionReady = sessionData !== null;

  // One-way latch: false → true when a terminal event arrives; never goes back to false.
  const isTerminalScreen =
    sessionData?.timeline.some(
      (e) =>
        (e.eventType === 'PAYMENT_COMPLETE' && e.completed) ||
        e.eventType === 'SESSION_CANCELLED' ||
        e.eventType === 'SESSION_EXPIRED'
    ) ?? false;

  /**
   * Get icon component based on event state
   */
  const getEventIcon = (event: TimelineEvent) => {
    if (event.failed) {
      return <CrossIcon />;
    }
    if (event.completed) {
      return <CheckIcon />;
    }
    return <ClockIcon />;
  };

  /**
   * Get CSS class for event status
   */
  const getEventStatusClass = (event: TimelineEvent): string => {
    if (event.failed) return styles.failed;
    if (event.completed) return styles.completed;
    return styles.pending;
  };

  /**
   * Copy payment link to clipboard
   */
  const handleCopyLink = async () => {
    if (!sessionData?.paymentLink) return;

    try {
      await navigator.clipboard.writeText(sessionData.paymentLink);
      setCopySuccess(true);
      setTimeout(
        () => setCopySuccess(false),
        UX_DELAYS.COPY_SUCCESS_NOTIFICATION
      );
    } catch {
      // Clipboard API blocked (e.g. inside an iframe without allow="clipboard-write").
      // Fall back to execCommand which works without the Permissions Policy.
      try {
        const textarea = document.createElement('textarea');
        textarea.value = sessionData.paymentLink;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (ok) {
          setCopySuccess(true);
          setTimeout(
            () => setCopySuccess(false),
            UX_DELAYS.COPY_SUCCESS_NOTIFICATION
          );
        } else {
          logger.error('Failed to copy to clipboard via fallback');
        }
      } catch (fallbackErr) {
        logger.error('Failed to copy to clipboard', fallbackErr);
      }
    }
  };

  /**
   * Fetch session data from BFF
   */
  const fetchSessionData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (useMockData) {
        // Mock data for testing - use mock scenarios
        let mockData: ChatSessionData;

        switch (mockScenario) {
          case 'saved-card':
            mockData = mockScenarios.savedCardSuccess(sessionId, customerId);
            break;

          case 'apple-pay':
            mockData = mockScenarios.applePaySuccess(sessionId, customerId);
            break;

          case 'google-pay':
            mockData = mockScenarios.googlePaySuccess(sessionId, customerId);
            break;

          case 'paypal':
            mockData = mockScenarios.paypalSuccess(sessionId, customerId);
            break;

          case 'decline':
            mockData = mockScenarios.paymentDecline(sessionId, customerId);
            break;

          case 'expire':
            mockData = mockScenarios.sessionExpire(sessionId, customerId);
            break;

          case 'error-retry':
            mockData = mockScenarios.errorRetryLater(sessionId, customerId);
            break;

          case 'error-declined':
            mockData = mockScenarios.errorDontRetry(sessionId, customerId);
            break;

          case 'error-max-attempts':
            mockData = mockScenarios.errorMaxAttempts(sessionId, customerId);
            break;

          case 'new-card':
          default:
            mockData = mockScenarios.newCardSuccess(sessionId, customerId);
            break;
        }

        setSessionData(mockData);
      } else {
        // Real mode: build initial ChatSessionData from props (BFF-injected values).
        // Show the 4-step timeline immediately with LINK_CREATED already completed
        // (the link exists in the session) and the remaining steps as pending.
        // SSE events will update each step as the customer progresses.
        const now = new Date().toISOString();
        const initialTimeline: TimelineEvent[] = [
          {
            eventType: 'LINK_CREATED',
            status: 'Link created',
            timestamp: now,
            completed: true,
            transactionId: sessionId,
          },
          {
            eventType: 'LINK_OPENED',
            status: 'Link opened',
            timestamp: now,
            completed: false,
            transactionId: sessionId,
          },
          {
            eventType: 'PAYMENT_METHOD_SELECTED',
            status: 'Payment method selected',
            timestamp: now,
            completed: false,
            transactionId: sessionId,
          },
          {
            eventType: 'PAYMENT_COMPLETE',
            status: 'Payment complete',
            timestamp: now,
            completed: false,
            transactionId: sessionId,
          },
        ];
        const data: ChatSessionData = {
          sessionId,
          customerId,
          amount: _amount,
          currency: _currency,
          interactionType: 'CHAT',
          paymentLink: initialSession?.paymentLink ?? '',
          timeline: initialTimeline,
          paymentStatus: 'INITIATED',
          expiresAt: initialSession?.expiresAt ?? now,
          createdAt: now,
          updatedAt: now,
        };
        logger.info('[ChatTimeline] Initialised session data from props', {
          sessionId,
        });
        setSessionData(data);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load session data';
      logger.error('[ChatTimeline] Error fetching session', err, { sessionId });
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setLoading(false);
    }
  }, [
    sessionId,
    customerId,
    _amount,
    _currency,
    initialSession,
    useMockData,
    mockScenario,
    onError,
  ]);

  /**
   * Handle new timeline event from SSE
   *
   * Supports both:
   * - Adding new events
   * - Updating existing events (e.g., marking as completed/incomplete)
   */
  const handleTimelineEvent = useCallback(
    (event: TimelineEvent) => {
      // LINK_OPENED completed:true signals the customer re-opened the payment
      // screen (e.g. after a decline and "Go back"). Reset to the 4-step base
      // timeline so the agent sees a clean slate for the new attempt.
      //
      // Guards (evaluated inside setSessionData against current state):
      // 1. Terminal success — SSE disconnects on success, so this is defensive.
      // 2. Internal navigation — the Checkout UI emits LINK_OPENED on every page
      //    mount, including the internal `/` → `/payment/new-card` navigation.
      //    That is NOT a new attempt: PAYMENT_METHOD_SELECTED is already
      //    completed and PAYMENT_COMPLETE has no failure yet. Resetting here
      //    would flash the agent's timeline back to the baseline mid-flow.
      //    Condition: PAYMENT_METHOD_SELECTED completed:true AND
      //    PAYMENT_COMPLETE not failed → this is an internal screen transition,
      //    skip the reset.
      //
      // The reset is deferred 150ms so that if the next SSE event (e.g.
      // PAYMENT_METHOD_SELECTED) arrives in the same burst, the pending timer is
      // cancelled and both the reset + follow-on update are applied in the same
      // React render — preventing a visible flash of the empty baseline timeline.
      if (event.eventType === 'LINK_OPENED' && event.completed) {
        if (linkOpenedResetTimerRef.current) {
          clearTimeout(linkOpenedResetTimerRef.current);
        }
        completionEmittedRef.current = false;
        linkOpenedResetTimerRef.current = setTimeout(() => {
          linkOpenedResetTimerRef.current = null;
          const hadDeclined = hasDeclinedRef.current;
          hasDeclinedRef.current = false;
          setSessionData((prev) =>
            applyLinkOpenedReset(prev, sessionId, hadDeclined)
          );
        }, 150);
        return;
      }

      // If a non-LINK_OPENED event arrives while a reset is pending, cancel the
      // deferred reset and apply it synchronously first so the follow-on event
      // lands on the fresh baseline in the same React render cycle.
      if (linkOpenedResetTimerRef.current) {
        clearTimeout(linkOpenedResetTimerRef.current);
        linkOpenedResetTimerRef.current = null;
        const hadDeclined = hasDeclinedRef.current;
        hasDeclinedRef.current = false;
        setSessionData((prev) =>
          applyLinkOpenedReset(prev, sessionId, hadDeclined)
        );
      }

      // Iframe retry fallback: LINK_OPENED is not re-emitted when Checkout UI navigates
      // client-side (vike SPA routing). When PAYMENT_METHOD_SELECTED arrives after a
      // genuine decline (CARD_NUMBER_ENTERED was completed), reset to baseline so the
      // agent sees a clean slate for the new attempt.
      // Guard: hadPreviousAttempt (CARD_NUMBER_ENTERED completed) is sufficient — the
      // Checkout UI alreadyTriggered guard ensures NEW_CARD_SELECTED fires only once per
      // mount, so spurious repeats mid-attempt no longer occur.
      if (event.eventType === 'PAYMENT_METHOD_SELECTED' && event.completed) {
        setSessionData((prev) => {
          if (!prev) return prev;
          const hadPreviousAttempt = prev.timeline.some(
            (e) => e.eventType === 'CARD_NUMBER_ENTERED' && e.completed
          );
          if (!hadPreviousAttempt) return prev;
          const hasTerminalSuccess = prev.timeline.some(
            (e) => e.eventType === 'PAYMENT_COMPLETE' && e.completed
          );
          if (hasTerminalSuccess) return prev;
          const linkCreatedEvent = prev.timeline.find(
            (e) => e.eventType === 'LINK_CREATED'
          );
          const now = new Date().toISOString();
          return {
            ...prev,
            paymentStatus: 'INITIATED',
            timeline: buildBaselineTimeline(linkCreatedEvent, sessionId, now),
          };
        });
        completionEmittedRef.current = false;
        hasDeclinedRef.current = false;
      }

      setSessionData((prev) => {
        if (!prev) return prev;

        // Find if event of this type already exists
        const existingIndex = prev.timeline.findIndex(
          (e) => e.eventType === event.eventType
        );

        let updatedTimeline: TimelineEvent[];

        if (existingIndex !== -1) {
          // Event exists - UPDATE it (e.g., change completed status)
          updatedTimeline = [...prev.timeline];
          updatedTimeline[existingIndex] = {
            ...updatedTimeline[existingIndex],
            ...event,
            timestamp: new Date().toISOString(),
          };
        } else {
          // Event doesn't exist - ADD it
          updatedTimeline = [...prev.timeline, event];
        }

        // When NEW_CARD is selected, insert card-entry steps before PAYMENT_COMPLETE
        // if they don't already exist. SAVED_CARD/wallets go straight to payment.
        if (
          event.eventType === 'PAYMENT_METHOD_SELECTED' &&
          event.paymentMethod === 'NEW_CARD'
        ) {
          const hasCardFields = updatedTimeline.some(
            (e) =>
              e.eventType === 'CARD_NUMBER_ENTERED' ||
              e.eventType === 'CARDHOLDER_NAME_ENTERED' ||
              e.eventType === 'EXPIRY_ENTERED' ||
              e.eventType === 'CVC_ENTERED'
          );
          if (!hasCardFields) {
            const paymentCompleteIdx = updatedTimeline.findIndex(
              (e) => e.eventType === 'PAYMENT_COMPLETE'
            );
            const cardFields: TimelineEvent[] = [
              {
                eventType: 'CARD_NUMBER_ENTERED',
                status: 'Card number entered',
                timestamp: new Date().toISOString(),
                completed: false,
                transactionId: sessionId,
              },
              {
                eventType: 'CARDHOLDER_NAME_ENTERED',
                status: 'Name on card entered',
                timestamp: new Date().toISOString(),
                completed: false,
                transactionId: sessionId,
              },
              {
                eventType: 'EXPIRY_ENTERED',
                status: 'Expiration date entered',
                timestamp: new Date().toISOString(),
                completed: false,
                transactionId: sessionId,
              },
              {
                eventType: 'CVC_ENTERED',
                status: 'CVC entered',
                timestamp: new Date().toISOString(),
                completed: false,
                transactionId: sessionId,
              },
            ];
            if (paymentCompleteIdx !== -1) {
              updatedTimeline.splice(paymentCompleteIdx, 0, ...cardFields);
            } else {
              updatedTimeline.push(...cardFields);
            }
          }
        } else if (event.eventType === 'PAYMENT_METHOD_SELECTED') {
          // Switching away from NEW_CARD — remove card-entry steps that were
          // injected for the previous selection.
          updatedTimeline = updatedTimeline.filter(
            (e) =>
              e.eventType !== 'CARD_NUMBER_ENTERED' &&
              e.eventType !== 'CARDHOLDER_NAME_ENTERED' &&
              e.eventType !== 'EXPIRY_ENTERED' &&
              e.eventType !== 'CVC_ENTERED'
          );
        }

        // Consistency guard: LINK_OPENED is sometimes skipped when the Checkout
        // UI opens directly on the card form (SPA routing, no method-selection
        // screen). If any downstream checkout event is already completed/failed,
        // the link must have been opened — backfill it so the timeline is coherent.
        const linkOpenedIdx = updatedTimeline.findIndex(
          (e) => e.eventType === 'LINK_OPENED'
        );
        if (linkOpenedIdx !== -1 && !updatedTimeline[linkOpenedIdx].completed) {
          const hasDownstreamChecked = updatedTimeline.some(
            (e) =>
              IMPLIES_LINK_OPENED.has(e.eventType) && (e.completed || e.failed)
          );
          if (hasDownstreamChecked) {
            updatedTimeline = [...updatedTimeline];
            updatedTimeline[linkOpenedIdx] = {
              ...updatedTimeline[linkOpenedIdx],
              completed: true,
            };
          }
        }

        // Update payment status if provided
        const updatedStatus = event.paymentStatus || prev.paymentStatus;

        return {
          ...prev,
          timeline: updatedTimeline,
          paymentStatus: updatedStatus,
          updatedAt: new Date().toISOString(),
        };
      });

      // On SUCCESS via SSE, emit the payment-outcome postMessage immediately so
      // the CRM knows without waiting for the agent. Declines are intentionally
      // NOT emitted here — for a declined payment the DECLINED postMessage is
      // sent on the Complete button click (handleComplete), per PAS-32538 so the
      // button has an explicit effect. Navigation (onSessionComplete) is only
      // triggered by the button click, never here.
      if (event.eventType === 'PAYMENT_COMPLETE' && event.completed) {
        if (!completionEmittedRef.current) {
          completionEmittedRef.current = true;
          window.parent.postMessage(
            {
              eventName: 'payment-outcome',
              type: 'payment.completed',
              payload: {
                paymentId: initialSession?.paymentId ?? sessionId,
                status: event.paymentStatus ?? 'COMPLETED',
                timestamp: new Date().toISOString(),
              },
            },
            postMessageTargetOrigin
          );
        }
      }
    },
    [initialSession, sessionId, postMessageTargetOrigin]
  );

  /**
   * Get event sequence based on scenario
   */
  const getEventSequence = useCallback((): TimelineEvent[] => {
    // For error scenarios, return the timeline from sessionData (already complete with all events)
    if (
      (mockScenario === 'error-retry' ||
        mockScenario === 'error-declined' ||
        mockScenario === 'error-max-attempts') &&
      sessionData
    ) {
      return sessionData.timeline;
    }

    // For other scenarios, use base events (4-event flow)
    const baseEvents: TimelineEvent[] = [
      {
        eventType: 'LINK_CREATED',
        status: 'Link created',
        timestamp: new Date().toISOString(),
        completed: true,
        transactionId: sessionId,
      },
      {
        eventType: 'LINK_OPENED',
        status: 'Link opened',
        timestamp: new Date().toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_METHOD_SELECTED',
        status: 'Payment method selected',
        timestamp: new Date().toISOString(),
        completed: false,
        transactionId: sessionId,
      },
      {
        eventType: 'PAYMENT_COMPLETE',
        status: 'Payment complete',
        timestamp: new Date().toISOString(),
        completed: false,
        transactionId: sessionId,
      },
    ];

    // Customize based on scenario
    switch (mockScenario) {
      case 'saved-card':
        return baseEvents.map((event) =>
          event.eventType === 'PAYMENT_METHOD_SELECTED'
            ? { ...event, paymentMethod: 'SAVED_CARD' as const }
            : event.eventType === 'PAYMENT_COMPLETE'
              ? { ...event, paymentStatus: 'SETTLED' as const }
              : event
        );

      case 'apple-pay':
        return baseEvents.map((event) =>
          event.eventType === 'PAYMENT_METHOD_SELECTED'
            ? { ...event, paymentMethod: 'APPLE_PAY' as const }
            : event.eventType === 'PAYMENT_COMPLETE'
              ? { ...event, paymentStatus: 'SETTLED' as const }
              : event
        );

      case 'google-pay':
        return baseEvents.map((event) =>
          event.eventType === 'PAYMENT_METHOD_SELECTED'
            ? { ...event, paymentMethod: 'GOOGLE_PAY' as const }
            : event.eventType === 'PAYMENT_COMPLETE'
              ? { ...event, paymentStatus: 'SETTLED' as const }
              : event
        );

      case 'paypal':
        return baseEvents.map((event) =>
          event.eventType === 'PAYMENT_METHOD_SELECTED'
            ? { ...event, paymentMethod: 'PAYPAL' as const }
            : event.eventType === 'PAYMENT_COMPLETE'
              ? { ...event, paymentStatus: 'SETTLED' as const }
              : event
        );

      case 'decline': {
        // Full event sequence: link → method → card fields → payment declined
        const now = new Date();
        return [
          {
            eventType: 'LINK_CREATED',
            status: 'Link created',
            timestamp: now.toISOString(),
            completed: true,
            transactionId: sessionId,
          },
          {
            eventType: 'LINK_OPENED',
            status: 'Link opened',
            timestamp: new Date(now.getTime() + 1000).toISOString(),
            completed: false,
            transactionId: sessionId,
          },
          {
            eventType: 'PAYMENT_METHOD_SELECTED',
            status: 'Payment method selected: New card',
            timestamp: new Date(now.getTime() + 2000).toISOString(),
            completed: false,
            paymentMethod: 'NEW_CARD' as const,
            transactionId: sessionId,
          },
          {
            eventType: 'CARDHOLDER_NAME_ENTERED',
            status: 'Name on card entered',
            timestamp: new Date(now.getTime() + 3000).toISOString(),
            completed: false,
            transactionId: sessionId,
          },
          {
            eventType: 'CARD_NUMBER_ENTERED',
            status: 'Card number entered',
            timestamp: new Date(now.getTime() + 4000).toISOString(),
            completed: false,
            transactionId: sessionId,
          },
          {
            eventType: 'EXPIRY_ENTERED',
            status: 'Expiration date entered',
            timestamp: new Date(now.getTime() + 5000).toISOString(),
            completed: false,
            transactionId: sessionId,
          },
          {
            eventType: 'CVC_ENTERED',
            status: 'CVC entered',
            timestamp: new Date(now.getTime() + 6000).toISOString(),
            completed: false,
            transactionId: sessionId,
          },
          {
            eventType: 'PAYMENT_PROCESSING',
            status: 'Payment processing',
            timestamp: new Date(now.getTime() + 7000).toISOString(),
            completed: false,
            transactionId: sessionId,
          },
          {
            eventType: 'PAYMENT_COMPLETE',
            status: 'Payment declined',
            timestamp: new Date(now.getTime() + 8000).toISOString(),
            completed: false,
            failed: true,
            paymentStatus: 'DECLINED' as const,
            transactionId: sessionId,
            errorCode: 'CARD_DECLINED',
            errorMessage:
              'Card declined by issuing bank. Please try a different payment method.',
          },
        ] as TimelineEvent[];
      }

      case 'expire':
        // Use base events - PAYMENT_COMPLETE will be transformed into SESSION_EXPIRED during progression
        return baseEvents;

      case 'new-card':
      default:
        return baseEvents.map((event) =>
          event.eventType === 'PAYMENT_METHOD_SELECTED'
            ? { ...event, paymentMethod: 'NEW_CARD' as const }
            : event.eventType === 'PAYMENT_COMPLETE'
              ? { ...event, paymentStatus: 'SETTLED' as const }
              : event
        );
    }
  }, [mockScenario, sessionId, sessionData]);

  /**
   * Mock timeline progression for demo/testing
   * Creates all events upfront as pending, then completes them one by one
   */
  useEffect(() => {
    if (!useMockData || !sessionData) return;

    // Stop progression if session is cancelled, expired, or payment failed
    const hasTerminalEvent = sessionData.timeline.some(
      (e) =>
        e.eventType === 'SESSION_CANCELLED' ||
        e.eventType === 'SESSION_EXPIRED' ||
        (e.eventType === 'PAYMENT_COMPLETE' && e.failed)
    );
    if (hasTerminalEvent) {
      // Clear any pending timer
      if (progressionTimerRef.current) {
        clearTimeout(progressionTimerRef.current);
        progressionTimerRef.current = null;
      }
      return;
    }

    // Get event sequence based on scenario
    const allEvents = getEventSequence();

    // On first load, create all events as pending (all as NOT failed initially)
    if (
      sessionData.timeline.length === 1 &&
      sessionData.timeline[0].eventType === 'LINK_CREATED'
    ) {
      setSessionData((prev) => {
        if (!prev) return prev;

        const pendingEvents: TimelineEvent[] = allEvents
          .slice(1)
          .map((evt) => ({
            eventType: evt.eventType,
            status: evt.status,
            timestamp: new Date().toISOString(),
            completed: false,
            failed: evt.failed || false, // Preserve failed flag from event sequence
            transactionId: sessionId,
            ...(evt.paymentStatus && { paymentStatus: evt.paymentStatus }),
            ...(evt.paymentMethod && { paymentMethod: evt.paymentMethod }),
            ...(evt.errorCode && { errorCode: evt.errorCode }),
            ...(evt.errorMessage && { errorMessage: evt.errorMessage }),
          }));

        return {
          ...prev,
          timeline: [...prev.timeline, ...pendingEvents],
        };
      });
      return;
    }

    // Find next pending event to complete
    const nextPendingIndex = sessionData.timeline.findIndex(
      (e) => !e.completed && !e.failed
    );

    // Special case: expire scenario - transform PAYMENT_COMPLETE into SESSION_EXPIRED
    if (mockScenario === 'expire' && nextPendingIndex !== -1) {
      const eventToCheck = sessionData.timeline[nextPendingIndex];

      // When it's time to complete PAYMENT_COMPLETE, transform it into SESSION_EXPIRED
      if (eventToCheck?.eventType === 'PAYMENT_COMPLETE') {
        const timer = setTimeout(() => {
          setSessionData((prev) => {
            if (!prev) return prev;

            const updatedTimeline = [...prev.timeline];
            // Transform PAYMENT_COMPLETE into SESSION_EXPIRED
            updatedTimeline[nextPendingIndex] = {
              eventType: 'SESSION_EXPIRED',
              status: 'Session expired',
              timestamp: new Date().toISOString(),
              completed: false,
              failed: true,
              paymentStatus: 'EXPIRED',
              transactionId: sessionId,
            };

            return {
              ...prev,
              timeline: updatedTimeline,
              paymentStatus: 'EXPIRED',
            };
          });
        }, 2000); // Normal 2-second delay

        progressionTimerRef.current = timer;
        return;
      }
    }

    // Stop progression if session expired
    if (
      mockScenario === 'expire' &&
      sessionData.timeline.some((e) => e.eventType === 'SESSION_EXPIRED')
    ) {
      return; // No more events to process
    }

    if (nextPendingIndex === -1) return; // All events completed

    // Complete next event after 2 seconds
    const timer = setTimeout(() => {
      setSessionData((prev) => {
        if (!prev) return prev;

        // Double-check: don't update if terminal event was added during timeout
        const hasTerminalNow = prev.timeline.some(
          (e) =>
            e.eventType === 'SESSION_CANCELLED' ||
            e.eventType === 'SESSION_EXPIRED' ||
            (e.eventType === 'PAYMENT_COMPLETE' && e.failed)
        );
        if (hasTerminalNow) {
          return prev; // Don't update
        }

        const updatedTimeline = [...prev.timeline];
        const eventToComplete = updatedTimeline[nextPendingIndex];

        // Check if this should be a failed event (from scenario definition)
        // Find matching event in allEvents by eventType (not by index, to avoid misalignment)
        const scenarioEvent = allEvents.find(
          (e) => e.eventType === eventToComplete.eventType
        );
        const shouldFail = scenarioEvent?.failed || false;

        // Update status text if it should fail
        let finalStatus =
          shouldFail && scenarioEvent
            ? scenarioEvent.status
            : eventToComplete.status;

        // Special case: When completing PAYMENT_METHOD_SELECTED, add the method to the status
        if (
          eventToComplete.eventType === 'PAYMENT_METHOD_SELECTED' &&
          scenarioEvent?.paymentMethod
        ) {
          const methodLabels: Record<PaymentMethodType, string> = {
            NEW_CARD: 'New card',
            SAVED_CARD: 'Saved card',
            APPLE_PAY: 'Apple Pay',
            GOOGLE_PAY: 'Google Pay',
            PAYPAL: 'PayPal',
          };
          finalStatus = `Payment method selected: ${methodLabels[scenarioEvent.paymentMethod]}`;

          // If NEW_CARD, insert 4 card entry events BEFORE Payment complete
          // BUT ONLY if they don't already exist (for base scenarios)
          // SKIP for expire scenario (session expires before card entry)
          if (
            scenarioEvent.paymentMethod === 'NEW_CARD' &&
            mockScenario !== 'expire'
          ) {
            // Check if card entry events already exist
            const hasCardEntryEvents = updatedTimeline.some(
              (e) =>
                e.eventType === 'CARD_NUMBER_ENTERED' ||
                e.eventType === 'CARDHOLDER_NAME_ENTERED' ||
                e.eventType === 'EXPIRY_ENTERED' ||
                e.eventType === 'CVC_ENTERED'
            );

            // Only insert if they don't exist (base 4-event scenarios)
            if (!hasCardEntryEvents) {
              // Find index of Payment complete event
              const paymentCompleteIndex = updatedTimeline.findIndex(
                (e) => e.eventType === 'PAYMENT_COMPLETE'
              );

              if (paymentCompleteIndex !== -1) {
                // Create 4 card entry events
                const cardEntryEvents: TimelineEvent[] = [
                  {
                    eventType: 'CARD_NUMBER_ENTERED',
                    status: 'Card number entered',
                    timestamp: new Date().toISOString(),
                    completed: false,
                    transactionId: sessionId,
                  },
                  {
                    eventType: 'CARDHOLDER_NAME_ENTERED',
                    status: 'Name on card entered',
                    timestamp: new Date().toISOString(),
                    completed: false,
                    transactionId: sessionId,
                  },
                  {
                    eventType: 'EXPIRY_ENTERED',
                    status: 'Expiration date entered',
                    timestamp: new Date().toISOString(),
                    completed: false,
                    transactionId: sessionId,
                  },
                  {
                    eventType: 'CVC_ENTERED',
                    status: 'CVC entered',
                    timestamp: new Date().toISOString(),
                    completed: false,
                    transactionId: sessionId,
                  },
                ];

                // Insert card entry events BEFORE Payment complete
                updatedTimeline.splice(
                  paymentCompleteIndex,
                  0,
                  ...cardEntryEvents
                );
              }
            }
          }
        }

        // Check if this is a PAYMENT_COMPLETE event with error (error scenarios)
        const isErrorEvent =
          eventToComplete.eventType === 'PAYMENT_COMPLETE' &&
          eventToComplete.errorCode;

        updatedTimeline[nextPendingIndex] = {
          ...eventToComplete,
          status: isErrorEvent ? 'Payment failed' : finalStatus, // Change status to "Payment failed" for errors
          completed: isErrorEvent ? false : !shouldFail, // Error events are not "completed"
          failed: isErrorEvent ? true : shouldFail, // Mark as failed if has errorCode
          timestamp: new Date().toISOString(),
          ...(scenarioEvent?.paymentMethod && {
            paymentMethod: scenarioEvent.paymentMethod,
          }),
          ...(scenarioEvent?.errorCode && {
            errorCode: scenarioEvent.errorCode,
          }),
          ...(scenarioEvent?.errorMessage && {
            errorMessage: scenarioEvent.errorMessage,
          }),
        };

        return {
          ...prev,
          timeline: updatedTimeline,
          paymentStatus:
            updatedTimeline[nextPendingIndex].paymentStatus ||
            prev.paymentStatus,
        };
      });
    }, 2000);

    progressionTimerRef.current = timer;

    return () => {
      clearTimeout(timer);
      progressionTimerRef.current = null;
    };
  }, [useMockData, sessionData, sessionId, mockScenario, getEventSequence]);

  /**
   * Handle payment failed event — update timeline in place instead of showing
   * a full-screen error overlay, so the agent can see the failure in context.
   * This applies to all error types (dont-retry, retry-later, max-attempts).
   */
  const handlePaymentFailed = useCallback(
    (data: PaymentFailedData) => {
      logger.warn('[ChatTimeline] Payment failed — updating timeline', data);

      if (data.errorCode === 'CARD_DECLINED') {
        // Intermediate decline — session remains open for retry.
        // Increment the counter shown in the decline banner and let
        // the subsequent LINK_OPENED reset the timeline for the next attempt.
        setDeclineCount((prev) => prev + 1);
        hasDeclinedRef.current = true;
        return;
      }

      handleTimelineEvent({
        eventType: 'PAYMENT_COMPLETE',
        status: 'Payment declined',
        timestamp: new Date().toISOString(),
        completed: false,
        failed: true,
        paymentStatus: 'DECLINED',
        errorCode: data.errorCode,
        errorMessage: data.errorMessage,
        transactionId: sessionId,
      });
    },
    [handleTimelineEvent, sessionId]
  );

  const handleGoBack = useCallback(() => {
    setErrorScreen(null);
  }, []);

  const handleSessionExpiredClose = useCallback(() => {
    handleSessionTermination({
      agentReturnUrl: initialSession?.agentReturnUrl,
      paymentId: initialSession?.paymentId ?? sessionId,
      status: 'SESSION_EXPIRED',
      sessionId,
    });
  }, [initialSession?.agentReturnUrl, initialSession?.paymentId, sessionId]);

  // Session expiry timer — driven by expiresAt from the Lambda BFF payload.
  // The backend does not guarantee a SESSION_EXPIRED SSE event, so we set our
  // own timer and transition to the session-expired error screen when TTL is reached.
  useEffect(() => {
    if (useMockData || !initialSession?.expiresAt) return;

    const delayMs = new Date(initialSession.expiresAt).getTime() - Date.now();

    if (delayMs <= 0) {
      setErrorScreen({ errorType: 'session-expired', attemptNumber: 0 });
      return;
    }

    const timerId = window.setTimeout(() => {
      setErrorScreen((prev) => {
        if (prev?.errorType === 'session-expired') return prev;
        return { errorType: 'session-expired', attemptNumber: 0 };
      });
      logger.warn('[ChatTimeline] Session expired (client-side timer)', {
        sessionId,
      });
    }, delayMs);

    return () => window.clearTimeout(timerId);
  }, [
    useMockData,
    initialSession?.expiresAt,
    initialSession?.agentReturnUrl,
    initialSession?.paymentId,
    sessionId,
  ]);

  /**
   * Setup SSE connection for real-time updates
   */
  useEffect(() => {
    if (useMockData || !isSessionReady || isTerminalScreen) return;

    // Use apiClient for authenticated SSE if available
    if (apiClient && isReady) {
      logger.info('[ChatTimeline] Setting up SSE via apiClient', { sessionId });

      try {
        const subscription = apiClient.createSSEConnection({
          endpoint: sseUrl || `/api/events/stream`,
          handlers: {
            onFieldCompleted: (data: FieldCompletedData) => {
              logger.info('[ChatTimeline] Field completed', data);

              // Fail-safe: when the Checkout UI opens directly on the new-card
              // form (no Google Pay / Apple Pay / saved-card selection screen),
              // PAYMENT_METHOD_SELECTED is never emitted. The card-entry sub-steps
              // are injected by handleTimelineEvent only when that event arrives,
              // so they end up appended after PAYMENT_COMPLETE instead of before
              // it. Synthesise the method-selected event the first time any card
              // field arrives while the step is still pending.
              const currentTimeline = sessionDataRef.current?.timeline ?? [];
              const methodStep = currentTimeline.find(
                (e) => e.eventType === 'PAYMENT_METHOD_SELECTED'
              );
              if (methodStep && !methodStep.completed) {
                handleTimelineEvent({
                  eventType: 'PAYMENT_METHOD_SELECTED',
                  status: 'Payment method selected: New card',
                  timestamp: new Date().toISOString(),
                  completed: true,
                  failed: false,
                  paymentMethod: 'NEW_CARD',
                  transactionId: sessionId,
                });
              }

              const eventType = mapFieldToTimelineEvent(data.field);
              const statusText = getFieldStatusText(data.field);
              const timelineEvent: TimelineEvent = {
                eventType,
                status: statusText,
                timestamp: new Date().toISOString(),
                completed: data.completed,
                failed: false,
                transactionId: sessionId,
              };
              handleTimelineEvent(timelineEvent);
            },
            onPaymentComplete: (data: PaymentCompleteData) => {
              logger.info('[ChatTimeline] Payment completed', data);
              const timelineEvent: TimelineEvent = {
                eventType: 'PAYMENT_COMPLETE',
                status: 'Payment complete',
                timestamp: new Date().toISOString(),
                completed: true,
                failed: false,
                paymentStatus: data.status,
                transactionId: data.transactionId ?? sessionId,
              };
              handleTimelineEvent(timelineEvent);
            },
            onProgress: (data: PaymentProgressData) => {
              logger.info('[ChatTimeline] Payment progress', data);
              const timelineEvent: TimelineEvent = {
                eventType: 'PAYMENT_PROCESSING',
                status: 'Payment processing',
                timestamp: new Date().toISOString(),
                completed: false,
                failed: false,
                transactionId: sessionId,
              };
              handleTimelineEvent(timelineEvent);
            },
            onTimelineEvent: (event: TimelineEventData) => {
              logger.info('[ChatTimeline] Timeline event received', event);
              const paymentMethod =
                event.paymentMethod as TimelineEvent['paymentMethod'];
              const methodLabels: Record<string, string> = {
                NEW_CARD: 'New card',
                SAVED_CARD: 'Saved card',
                APPLE_PAY: 'Apple Pay',
                GOOGLE_PAY: 'Google Pay',
                PAYPAL: 'PayPal',
              };
              // Backend does not send `status` for PAYMENT_METHOD_SELECTED —
              // derive it locally so the timeline row always has a label.
              const derivedStatus =
                event.status ||
                (event.eventType === 'PAYMENT_METHOD_SELECTED' && paymentMethod
                  ? `Payment method selected: ${methodLabels[paymentMethod] ?? paymentMethod}`
                  : event.eventType === 'LINK_OPENED'
                    ? 'Link opened'
                    : event.eventType);
              const timelineEvent: TimelineEvent = {
                eventType: event.eventType as TimelineEvent['eventType'],
                status: derivedStatus,
                timestamp: new Date().toISOString(),
                completed: event.completed,
                failed: event.failed,
                paymentMethod,
                declineReason: event.declineReason,
                transactionId: sessionId,
              };
              handleTimelineEvent(timelineEvent);
            },
            onPaymentFailed: handlePaymentFailed,
            onSessionExpired: () => {
              logger.warn('[ChatTimeline] Session expired (SSE event)', {
                sessionId,
              });
              setErrorScreen((prev) => {
                if (prev?.errorType === 'session-expired') return prev;
                return { errorType: 'session-expired', attemptNumber: 0 };
              });
            },
            onError: (error: Error) => {
              logger.error('[ChatTimeline] SSE error', error, { sessionId });
              onError?.(error);
            },
          },
          onReconnectFailed: () => {
            logger.error('[ChatTimeline] SSE reconnection exhausted', {
              sessionId,
            });
            setErrorScreen((prev) => {
              if (prev) return prev;
              return { errorType: 'sse-disconnected', attemptNumber: 0 };
            });
          },
        });

        sseSubscriptionRef.current = subscription;

        return () => {
          logger.info('[ChatTimeline] Cleaning up SSE connection', {
            sessionId,
          });
          subscription.unsubscribe();
        };
      } catch (err) {
        logger.error('[ChatTimeline] Failed to setup SSE', err, { sessionId });
      }
    } else {
      // Fallback to direct EventSource (backward compatibility)
      logger.info('[ChatTimeline] Setting up SSE via EventSource', {
        sessionId,
      });

      const sseEndpoint = sseUrl || `/api/events/stream`;

      try {
        const eventSource = new EventSource(sseEndpoint);

        eventSource.onopen = () => {
          logger.info('[ChatTimeline] SSE connected');
        };

        // Backend sends 'timeline_event' (lowercase). EventSource.addEventListener
        // is case-sensitive so we register both to be safe.
        const handleFallbackTimelineEvent = (event: Event) => {
          try {
            const data = JSON.parse((event as MessageEvent).data);
            handleTimelineEvent(data.data || data);
          } catch (err) {
            logger.error('[ChatTimeline] Failed to parse SSE event', err);
          }
        };
        eventSource.addEventListener(
          'timeline_event',
          handleFallbackTimelineEvent
        );
        eventSource.addEventListener(
          'TIMELINE_EVENT',
          handleFallbackTimelineEvent
        );

        eventSource.onerror = () => {
          logger.warn('[ChatTimeline] SSE connection error, auto-reconnecting');
        };

        return () => {
          eventSource.close();
        };
      } catch (err) {
        logger.error('[ChatTimeline] Failed to setup EventSource', err, {
          sessionId,
        });
      }
    }
  }, [
    sessionId,
    isSessionReady,
    isTerminalScreen,
    sseUrl,
    useMockData,
    apiClient,
    isReady,
    handleTimelineEvent,
    handlePaymentFailed,
    onError,
    initialSession?.agentReturnUrl,
    initialSession?.paymentId,
  ]);

  useEffect(() => {
    return () => {
      if (linkOpenedResetTimerRef.current) {
        clearTimeout(linkOpenedResetTimerRef.current);
      }
    };
  }, []);

  /**
   * Fetch (or build) session data on mount. In real mode this is synchronous —
   * no API call needed. In mock mode it uses the mock scenario generators.
   */
  useEffect(() => {
    fetchSessionData();
  }, [fetchSessionData]);

  /**
   * Handle cancel button click
   */
  const handleCancel = async () => {
    if (!sessionData) return;
    setCancelError(null);

    const confirmed = window.confirm(
      'Are you sure you want to cancel payment? This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      // Add cancelled event to timeline
      const cancelledEvent: TimelineEvent = {
        eventType: 'SESSION_CANCELLED',
        status: 'Payment cancelled',
        timestamp: new Date().toISOString(),
        completed: false,
        failed: true,
        paymentStatus: 'CANCELLED',
      };

      if (useMockData) {
        // Mock mode: just add the event locally
        setSessionData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            timeline: [...prev.timeline, cancelledEvent],
            paymentStatus: 'CANCELLED',
          };
        });

        onSessionCancel?.({
          sessionId,
          timeline: [...sessionData.timeline, cancelledEvent],
        });
      } else {
        if (!apiClient || !isReady) {
          throw new Error('API client not ready');
        }
        logger.info('[ChatTimeline] Cancelling payment via apiClient', {
          sessionId,
        });
        await apiClient.cancelPayment();

        // Update local state
        setSessionData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            timeline: [...prev.timeline, cancelledEvent],
            paymentStatus: 'CANCELLED',
          };
        });

        onSessionCancel?.({
          sessionId,
          timeline: [...sessionData.timeline, cancelledEvent],
        });
      }

      if (!initialSession?.agentReturnUrl) {
        window.parent.postMessage(
          {
            eventName: 'payment-cancelled',
            type: 'payment.cancelled',
            payload: {
              paymentId: initialSession?.paymentId ?? sessionId,
              sessionId,
              status: 'CANCELLED',
              timestamp: new Date().toISOString(),
            },
          },
          postMessageTargetOrigin
        );
      }
    } catch (err) {
      logger.error('[ChatTimeline] Failed to cancel session', err, {
        sessionId,
      });
      setCancelError('Failed to cancel payment. Please try again.');
    }
  };

  /**
   * Handle complete button click
   */
  const handleComplete = () => {
    if (!sessionData) return;

    // postMessage is deduplicated via completionEmittedRef. Success is emitted
    // by the SSE handler (CRM notified immediately); a decline is NOT emitted on
    // SSE, so its DECLINED postMessage is sent here on the Complete click
    // (PAS-32538). The guard still prevents a double emit if both paths run.
    if (!completionEmittedRef.current) {
      completionEmittedRef.current = true;
      const declined = sessionData.timeline.some(
        (e) => e.eventType === 'PAYMENT_COMPLETE' && e.failed
      );
      window.parent.postMessage(
        {
          eventName: declined ? 'integration-failure' : 'payment-outcome',
          type: 'payment.completed',
          payload: {
            paymentId: initialSession?.paymentId ?? sessionId,
            status: declined
              ? 'DECLINED'
              : (sessionData.paymentStatus ?? 'COMPLETED'),
            timestamp: new Date().toISOString(),
          },
        },
        postMessageTargetOrigin
      );
    }

    // Navigation is always triggered on explicit click — even if the SSE handler
    // already set completionEmittedRef (the SSE-triggered onSessionComplete call
    // may have been a no-op when agentReturnUrl was absent).
    onSessionComplete?.(sessionData.timeline);
  };

  /**
   * Check if session is complete (payment succeeded only — decline keeps Complete disabled)
   */
  const isSessionComplete = sessionData?.timeline.some(
    (e) => e.eventType === 'PAYMENT_COMPLETE' && e.completed
  );

  /**
   * Terminal decline — PAYMENT_COMPLETE marked failed. Distinct from an
   * intermediate CARD_DECLINED (retry still open), which never adds a failed
   * PAYMENT_COMPLETE entry.
   */
  const isSessionDeclined =
    sessionData?.timeline.some(
      (e) => e.eventType === 'PAYMENT_COMPLETE' && e.failed
    ) ?? false;

  /**
   * On a terminal decline the agent would otherwise be stuck (Complete disabled,
   * no way out). Enable Complete so they can leave — but only when there is an
   * exit path: redirect to agentReturnUrl, or a DECLINED postMessage when the
   * result page is suppressed. When resultPageEnabled is true (or absent) the
   * result page only applies to success, so a declined payment keeps Complete
   * disabled and the agent stays on the timeline (existing behaviour).
   */
  const canCompleteAfterDecline =
    isSessionDeclined &&
    (!!initialSession?.agentReturnUrl ||
      initialSession?.resultPageEnabled === false);

  /**
   * Check if session is active (not cancelled/expired/completed successfully)
   * Active means the session can still be cancelled
   * Note: Payment declined (failed) keeps cancel enabled
   */
  const isSessionActive = !sessionData?.timeline.some(
    (e) =>
      e.eventType === 'SESSION_CANCELLED' ||
      e.eventType === 'SESSION_EXPIRED' ||
      (e.eventType === 'PAYMENT_COMPLETE' && e.completed) ||
      (e.eventType === 'PAYMENT_COMPLETE' && e.failed)
  );

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} role="status" />
          <span>Loading session...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !sessionData) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Unable to load session</h2>
          <p>{error || 'Session not found'}</p>
          <button onClick={fetchSessionData} className={styles.completeButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show error screen if payment failed or session expired
  if (errorScreen) {
    return (
      <ErrorScreen
        errorConfig={errorScreen}
        onGoBack={handleGoBack}
        onAction={
          errorScreen.errorType === 'session-expired'
            ? handleSessionExpiredClose
            : errorScreen.errorType === 'sse-disconnected'
              ? () => window.location.reload()
              : undefined
        }
      />
    );
  }

  // Waiting state: SSE not yet received, no payment link and no timeline events.
  // Shown in real mode only — mock mode always starts with pre-populated data.
  const isWaiting =
    !useMockData &&
    !sessionData?.paymentLink &&
    (sessionData?.timeline ?? []).length === 0;

  if (isWaiting) {
    return (
      <div className={styles.container} data-testid="chat-timeline-waiting">
        <div className={styles.waitingState}>
          <div className={styles.waitingSpinner} aria-hidden="true" />
          <p className={styles.waitingMessage}>
            Waiting for the payment session to initialise…
          </p>
          <p className={styles.waitingSubMessage}>
            The payment link will appear here once the customer opens it.
          </p>
        </div>
        <div className={styles.actionButtons}>
          <button
            className={styles.cancelButton}
            onClick={handleCancel}
            aria-label="Cancel payment"
          >
            Cancel
          </button>
          <button
            className={styles.completeButton}
            disabled
            aria-label="Complete session"
          >
            Complete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="chat-timeline-ui">
      {/* Payment Link Section - Grid: Left (label+link) | Right (button) */}
      <div className={styles.paymentLinkSection}>
        <div className={styles.paymentLinkContainer}>
          <div className={styles.paymentLinkLabel}>Payment link:</div>
          <div className={styles.paymentLink}>{sessionData.paymentLink}</div>
        </div>
        <button
          className={styles.copyButton}
          onClick={handleCopyLink}
          aria-label="Copy payment link"
          title={copySuccess ? 'Copied!' : 'Copy link'}
        >
          {copySuccess ? <CheckIcon /> : <CopyIcon />}
        </button>
      </div>

      {/* Intermediate decline notification (PAS-32466). Shown from the first
          CARD_DECLINED onward; hidden on any terminal screen (success,
          cancelled, expired, or a terminal decline such as max-attempts). */}
      {declineCount > 0 && !isTerminalScreen && !isSessionDeclined && (
        <div className={styles.declineBanner} role="status" aria-live="polite">
          <div className={styles.declineBannerIcon} aria-hidden="true">
            <InfoIcon />
          </div>
          <span className={styles.declineBannerText}>
            Payment attempt: {declineCount} of 3
          </span>
        </div>
      )}

      {/* Timeline */}
      <div
        className={styles.timeline}
        role="list"
        aria-label="Payment timeline"
      >
        {sessionData.timeline.map((event, index) => (
          <div
            key={`${event.eventType}-${index}`}
            className={styles.timelineEvent}
            role="listitem"
          >
            <div className={styles.eventIcon} aria-hidden="true">
              {getEventIcon(event)}
            </div>
            <div className={styles.eventContent}>
              <div
                className={`${styles.eventStatus} ${getEventStatusClass(event)}`}
              >
                {event.status}
              </div>
              <div className={styles.eventTimestamp}>
                {event.completed || event.failed
                  ? formatTimestamp(event.timestamp)
                  : ''}
              </div>
            </div>
            <span className={styles.visuallyHidden}>
              {event.completed
                ? 'completed'
                : event.failed
                  ? 'failed'
                  : 'pending'}
              {(event.completed || event.failed) &&
                ` at ${formatTimestamp(event.timestamp)}`}
            </span>
          </div>
        ))}
      </div>

      {/* Cancel error — shown inline, does not replace the timeline */}
      {cancelError && (
        <p className={styles.cancelError} role="alert">
          {cancelError}
        </p>
      )}

      {/* Action Buttons */}
      <div className={styles.actionButtons}>
        <button
          className={styles.cancelButton}
          onClick={handleCancel}
          disabled={!isSessionActive}
          aria-label="Cancel payment"
        >
          Cancel
        </button>
        <button
          className={styles.completeButton}
          onClick={handleComplete}
          disabled={!(isSessionComplete || canCompleteAfterDecline)}
          aria-label="Complete session"
        >
          Complete
        </button>
      </div>
    </div>
  );
};
